import { useState } from 'react';
import { AiOutlinePercentage } from 'react-icons/ai';
import { toastSuccess } from '../utils/toasts';
import MenuDropdown from './atoms/MenuDropdown';
import CustomModal from './atoms/CustomModal';
import { useDeleteCouponMutation } from '../store';
import Button from './atoms/Button';
import CouponDTO from '../types/Coupon';
import CreateEditCoupon from './CreateEditCoupon';
import config from '../config/config';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import Sale01 from './icons/Sale01';

interface Props {
  coupons: CouponDTO[];
  eventId: string;
  businessId: string;
  button?: {
    label: string;
    reactIcon: React.ReactNode;
    onClick: () => void;
    variant: 'primary' | 'secondary' | 'tertiary';
  };
}

interface TableCoupons extends BaseData {
  id: string;
  code: JSX.Element;
  amount: string;
  value: string;
}

const CouponsTable: React.FC<Props> = ({
  coupons, eventId, businessId, button,
}) => {
  const { event } = useGetSelectedEvent();
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState(<div></div>);
  const [deleteCoupon, resultsDelete] = useDeleteCouponMutation();

  const handleConfirmDeleteCoupon = (coupon: CouponDTO) => {
    deleteCoupon({
      businessId,
      eventId,
      couponId: coupon.id,
    });
    setShowModal(false);
  };

  const handleClose = () => setShowModal(false);

  const handleDeleteCoupon = (coupon: CouponDTO) => {
    setModalBody(
      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-xl font-medium'>Eliminar cupón</h1>
          <p>
            ¿Deseas eliminar este cupón?
          </p>
          <p className='mt-2'>Una vez eliminado desaparecerá de la lista y no se podrá revertir.</p>
        </div>

        <div className='flex flex-row gap-2'>
          <Button

            variant="secondary"
            onClick={() => setShowModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => handleConfirmDeleteCoupon(coupon)}
          >
            Eliminar
          </Button>
        </div>
      </div>,
    );
    setShowModal(true);
  };

  const handleEditCoupon = (coupon: CouponDTO) => {
    setModalBody(
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-medium">Editar cupón</h1>
          <p className="text-text-tertiary text-sm">
            Podrás realizar las ediciones que necesites en este cupón
          </p>
        </div>
        <CreateEditCoupon coupon={coupon} onSuccess={handleClose} onCancel={() => setShowModal(false)} />
      </div>,

    );
    setShowModal(true);
  };

  const handleCopyCouponLink = (coupon: CouponDTO) => {
    navigator.clipboard
      .writeText(`${config.baseDomain}/events/${event?.slug}?coupon=${coupon.code}`)
      .then(() => {
        toastSuccess('Link copiado al portapapeles');
      })
      .catch((err) => {
        console.error('Error al copiar al portapapeles: ', err);
      });
  };

  const dropDownOptions = [
    {
      label: 'Copiar link',
      onClick: handleCopyCouponLink,
    },
    {
      label: 'Editar',
      onClick: handleEditCoupon,
    },
    {
      label: 'Eliminar',
      onClick: handleDeleteCoupon,
    },
  ];

  const tableConfig = [
    {
      label: 'Código',
      id: 'code',
    },
    {
      label: 'Estado',
      id: 'active',
    },
    {
      label: 'Cantidad',
      id: 'amount',
    },

    {
      label: 'Descuentos',
      id: 'value',
    },
  ];

  const actions = (row: TableCoupons) => {
    const coupon = coupons.find((c) => c.id === row.id);
    if (!coupon) {
      throw new Error('Coupon not found');
    }
    return (
      <MenuDropdown<CouponDTO>
        options={dropDownOptions}
        data={coupon}
        loading={resultsDelete.isLoading}
      />
    );
  };

  const customButton = button && <Button onClick={button.onClick} variant={button.variant}>
    <div className='flex flex-row gap-2 items-center'>
      <Sale01 className='min-w-[20px] min-h-[20px] w-5 h-5' />
      <p className='whitespace-nowrap hidden md:block'>{button.label}</p>
    </div>
  </Button>;

  const rows: TableCoupons[] = coupons.map((coupon) => ({
    id: coupon.id,
    code: <div className='whitespace-nowrap'>{coupon.code}</div>,
    active: coupon.active ? (
      <div className='flex flex-row gap-2'>
        <div className='tag-primary'>
          Habilitado
        </div>
        {coupon.limit_amount - coupon.taken_amount <= 10 && coupon.limit_amount - coupon.taken_amount > 0 && <div className='tag-secondary'>
          Quedan pocos
        </div>}
        {coupon.limit_amount - coupon.taken_amount <= 0 && <div className='tag-secondary'>
          Cupones agotados
        </div>}
      </div>

    ) : (
      <div className='tag-warning'>
        No habilitado
      </div>
    ),
    amount: `${coupon.taken_amount} / ${coupon.limit_amount}`,
    value: coupon.type === 'percentage' ? `${coupon.value * 100}%` : `$${coupon.value}`,

  }));

  return (
    <div>
      {coupons.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={tableConfig}
          pagination={{
            enabled: true,
            pageSize: 10,
          }}
          searchEnabled={true}
          actions={actions}
          customButtons={customButton ? [customButton] : []}
          tableId="coupons-table"
        />
      ) : (
        <EmptyTable
          title="Aún no hay cupones en este evento"
          info="Aquí aparecerán todos los cupones que crees en este evento"
          icon={AiOutlinePercentage}
          iconColor="text-secondary-500"
          button={button ? [
            {
              label: button.label,
              reactIcon: button.reactIcon,
              onClick: button.onClick,
              variant: button.variant,
            },
          ] : undefined}
        />
      )}
      <CustomModal
        onRequestClose={handleClose}
        isOpen={showModal}
        hideCancelButton={true}
      >
        {modalBody}
      </CustomModal>
    </div>
  );
};

export default CouponsTable;
